import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Icon } from "rhombus-react";

import { addPage } from "reducers/VersionGeneration";
import ProgressContainer from "widgets/ProgressContainer";

import PreviewGroup from "./PreviewGroup";
import Sparkler from "./Sparkler";
import SelectedVersions from "./SelectedVersions";

import "./GeneratedPreviews.css";

function generateVariationKey(variation){
	/*
		We need a React key to uniquely identify variations, which are objects keyed by the
		variable name, and a string value, in order to prevent expensive rerenders of
		preview iframes, etc., when we do things like delete generated assets (and the variations
		that use them), or add more pages.
	*/
	return JSON.stringify(variation);
}

const RPP = 10;

export default function GeneratedPreviews(props){
	const {
		showSelected,
	} = props;
	const dispatch = useDispatch();
	const {
		creatives,
		isGeneratingAssets,
		numVariationsShown,
	} = useSelector(state => state.versionGeneration || {});

	const variations = useSelector(state => state.versionGeneration.variations);

	const previews = useMemo(() => {
		return variations?.slice(0, numVariationsShown).map((variation, index) => {
			if(Object.keys(variation).length === 0){
				// This can happen if the user deletes all of the generated assets
				return null;
			}

			const name = `Variation ${index + 1}`;
			const key = generateVariationKey(variation);
			return (
				<PreviewGroup
					key={key}
					variationIndex={index}
					name={name}
					variables={variation}
					isGenerating={isGeneratingAssets}
				/>
			);
		});
	}, [variations, numVariationsShown, isGeneratingAssets]);

	if(isGeneratingAssets){
		return (
			<div className="generating-previews">
				{creatives.map((v) => {
					return (
						<aside className="placeholder-preview" key={v.id}>
							<div className="empty-space" style={{
								width:`${v.width}px`,
								height:`${v.height}px`,
							}}>
								<Sparkler />
							</div>
							<span>{v.width}x{v.height}</span>
							<div className="preview-controls">
								<Icon name="far fa-sync-alt" />
								<Icon name="far fa-frame" />
							</div>
						</aside>
					);
				})}
			</div>
		);
	}

	const remaining = variations.length - numVariationsShown;
	const loadMoreLabel = (remaining === 1) ? "Next Version" : `Next ${Math.min(RPP, remaining)} Variations`;
	const loadMore = (remaining === 0) ? null : (<div className="load-more">
		<button
			onClick={() => dispatch(addPage())}
			className="cypress-gen-ai-load-more-button"
			type="button"
		>
			{loadMoreLabel}
		</button>
	</div>);

	return (
		<ProgressContainer isLoading={false}>
			<div className="previews">
				{previews}
				{loadMore}
			</div>
			<div className={`selected-container ${showSelected ? "visible" : "hidden"}`}>
				<SelectedVersions />
			</div>
		</ProgressContainer>
	);
}

GeneratedPreviews.displayName = "GeneratedPreviews";
